/**
 * useScreenAnnouncements Hook
 * Ekran için aktif duyuruları yönetir (header marquee'sinde gösterilir).
 * - GET /api/QuickSaleAnnouncement/for-screen/{screenId}?branchId=...
 * - Ekrana girişte bir kez, sonra 5 dakikada bir sorgulanır (duyurunun anında
 *   görünmesi/kalkması gerekmez; biten duyuru en geç sonraki yenilemede düşer).
 * - SignalR `OnAnnouncementChanged` event'inde (duyuru eklendi/değişti) refetch.
 *
 * Not: Kiosk ürün görünürlük SignalR bağlantısı (kioskSignalRService.connect)
 * tarihsel olarak bu effect içinde kurulduğu için davranış korunarak burada bırakıldı.
 */

import { useState, useEffect } from 'react';
import { announcementApi, QuickSaleAnnouncementDto } from '../../../../services/announcementApiService';
import announcementSignalRService from '../../../../services/announcementSignalRService';
import kioskSignalRService from '../../../../services/kioskSignalRService';

/** Periyodik yenileme aralığı. */
const REFRESH_MS = 5 * 60 * 1000;

export interface UseScreenAnnouncementsParams {
  /** Şirket ID */
  companyId: string | null;
  /** Aktif ekran ID (currentScreen?.id) */
  screenId: string | undefined;
  /** Ekranın şube ID'si (currentScreen?.branchId) */
  screenBranchId: string | null | undefined;
  /** Kullanıcının şube ID'si (quickSaleUserData?.branchesId) */
  userBranchesId: string | null | undefined;
}

export interface UseScreenAnnouncementsReturn {
  activeAnnouncements: QuickSaleAnnouncementDto[];
}

/**
 * Ekran duyuru sistemi hook'u
 */
export const useScreenAnnouncements = ({
  companyId,
  screenId,
  screenBranchId,
  userBranchesId,
}: UseScreenAnnouncementsParams): UseScreenAnnouncementsReturn => {
  const [activeAnnouncements, setActiveAnnouncements] = useState<QuickSaleAnnouncementDto[]>([]);

  useEffect(() => {
    if (!companyId || !screenId) {
      console.log('📢 [QuickSaleScreen Announcements] ⏸️ Bekleniyor:', { companyId: !!companyId, currentScreenId: screenId });
      return;
    }

    let cancelled = false;
    const numericScreenId = parseInt(String(screenId), 10);
    if (isNaN(numericScreenId)) {
      console.warn('📢 [QuickSaleScreen Announcements] ⚠️ ScreenId numeric değil, fetch iptal:', screenId);
      return;
    }
    const branchIdForFetch = screenBranchId || userBranchesId || null;
    console.log('📢 [QuickSaleScreen Announcements] 🚀 Fetch başlıyor:', {
      companyId,
      screenId: numericScreenId,
      branchId: branchIdForFetch,
    });

    const fetchAnnouncements = async () => {
      try {
        const list = await announcementApi.getActiveForScreen(numericScreenId, branchIdForFetch);
        console.log('📢 [QuickSaleScreen Announcements] ✅ Response:', {
          count: list?.length ?? 0,
          items: list,
        });
        if (!cancelled) setActiveAnnouncements(list);
      } catch (err) {
        console.error('📢 [QuickSaleScreen Announcements] ❌ Fetch hatası:', err);
      }
    };

    fetchAnnouncements();

    // Periyodik yenileme (5 dk): günlük saat aralığı / gün geçişleri ve biten duyurular için.
    const interval = window.setInterval(fetchAnnouncements, REFRESH_MS);

    // SignalR canlı tetikleme
    const cbId = `quicksale_screen_${numericScreenId}`;
    console.log('📢 [QuickSaleScreen Announcements] 🔌 SignalR connect çağrılıyor:', { companyId, cbId });
    announcementSignalRService
      .connect(companyId)
      .then(() => {
        console.log('📢 [QuickSaleScreen Announcements] 🔌 SignalR connect tamamlandı. State:', announcementSignalRService.getConnectionState());
      })
      .catch((err) => {
        console.error('📢 [QuickSaleScreen Announcements] 🔌 SignalR connect hatası:', err);
      });
    announcementSignalRService.onAnnouncementChanged(cbId, (payload) => {
      console.log('📢 [QuickSaleScreen Announcements] 🔔 SignalR event alındı, refetching:', payload);
      fetchAnnouncements();
    });

    // 🖥️ Plan 2 — Kiosk Urun Gorunurluk SignalR baglantisi (paralel ekleme).
    // KioskProductsModal henuz acilmamis olsa bile burada baglanmak, modal acildiginda
    // anlik state'i hazir tutar; ek olarak diger admin sekmelerinde modal acik ise
    // cross-tab senkronizasyon hemen calisir.
    kioskSignalRService
      .connect(companyId)
      .catch((err) => {
        console.error('🖥️ [QuickSaleScreen KioskVis] 🔌 SignalR connect hatası:', err);
      });

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      announcementSignalRService.removeAnnouncementChangedCallback(cbId);
    };
  }, [companyId, screenId, screenBranchId, userBranchesId]);

  return { activeAnnouncements };
};

export default useScreenAnnouncements;
